import { useTranslation } from 'react-i18next';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  OverlayDrawer,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useActiveTheme } from '../theme/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import type { IWorkAvailabilityServices } from './types';
import WorkAvailabilityCreate from './WorkAvailabilityCreate';
import WorkAvailabilityEdit from './WorkAvailabilityEdit';

const DRAWER_MODES = {
  create: {
    title: ['createAvailability', 'Create availability'],
    submit: ['create', 'Create'],
    Content: WorkAvailabilityCreate,
  },
  edit: {
    title: ['editAvailability', 'Edit availability'],
    submit: ['save', 'Save'],
    Content: WorkAvailabilityEdit,
  },
} as const;

const CreateContent = DRAWER_MODES.create.Content;
const EditContent = DRAWER_MODES.edit.Content;

export interface WorkAvailabilityDrawerProps {
  open: boolean;
  mode: keyof typeof DRAWER_MODES;
  workId: number;
  windowId: number | null;
  services: IWorkAvailabilityServices;
  onClose: () => void;
  onSaved: () => void;
}

const WorkAvailabilityDrawer = ({
  open,
  mode,
  workId,
  windowId,
  services,
  onClose,
  onSaved,
}: WorkAvailabilityDrawerProps) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const config = DRAWER_MODES[mode];

  return (
    <FluentProvider theme={theme}>
      <OverlayDrawer
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) onClose();
        }}
        position="end"
        size={isMobile ? 'full' : 'medium'}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {t(config.title[0], config.title[1])}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          {open && mode === 'create' && (
            <CreateContent
              workId={workId}
              services={services}
              submitLabel={t(
                DRAWER_MODES.create.submit[0],
                DRAWER_MODES.create.submit[1]
              )}
              onClose={onClose}
              onSaved={onSaved}
            />
          )}
          {open && mode === 'edit' && windowId != null && (
            <EditContent
              workId={workId}
              windowId={windowId}
              services={services}
              submitLabel={t(
                DRAWER_MODES.edit.submit[0],
                DRAWER_MODES.edit.submit[1]
              )}
              onClose={onClose}
              onSaved={onSaved}
            />
          )}
        </DrawerBody>
      </OverlayDrawer>
    </FluentProvider>
  );
};

export default WorkAvailabilityDrawer;
